import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useLoginForm, LoginFormValues } from "@/hooks/useLoginForm";
import { useLoginAuth } from "@/hooks/useLoginAuth";

export const LoginForm = () => {
  const { t } = useTranslation(["auth", "common"]);
  const form = useLoginForm();
  const { isLoading, handleLogin } = useLoginAuth();
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (values: LoginFormValues) => {
    await handleLogin(values.email, values.password);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="login-label">{t("email", { ns: "auth" })}</FormLabel>
              <div className="relative">
                <Mail className="login-field-icon left-4" aria-hidden />
                <FormControl>
                  <Input placeholder="you@example.com" {...field} autoComplete="email" type="email"
                    inputMode="email" className="login-input pl-12" />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="login-label">{t("password", { ns: "auth" })}</FormLabel>
              <div className="relative">
                <Lock className="login-field-icon left-4" aria-hidden />
                <FormControl>
                  <Input type={showPassword ? "text" : "password"} placeholder="••••••••" {...field}
                    autoComplete="current-password" className="login-input pl-12 pr-12" />
                </FormControl>
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  className="login-eye absolute right-2 top-1/2 -translate-y-1/2"
                  aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="text-right text-sm">
          <Link to="/forgot-password" className="login-forgot hover:underline">
            {t("forgotPassword", { ns: "auth" })}
          </Link>
        </div>

        <Button type="submit" className="login-submit w-full" disabled={isLoading}>
          {isLoading ? t("loading", { ns: "common" }) : t("login", { ns: "auth" })}
          {!isLoading && <ArrowRight className="ml-2 h-5 w-5" aria-hidden />}
        </Button>
      </form>
    </Form>
  );
};
